import { RotateCw, Undo2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import useBulkOrderActions, { BulkSourceRow } from '@/hooks/useBulkOrderActions';
import { BulkActionDialog } from './BulkActionDialog';

interface BulkOrderActionsProps {
  rows: BulkSourceRow[];
  /** After the merchant closes the result view: refresh the list, clear the selection. */
  onDone: () => void;
}

/**
 * BOXY-376 — [Resend (n)] [Request RTO (n)] on the orders list selection bar.
 * The buttons show only when a selected order qualifies for one of them.
 */
export function BulkOrderActions({ rows, onDone }: BulkOrderActionsProps) {
  const { t } = useTranslation();
  const bulk = useBulkOrderActions(rows);

  const close = () => {
    const finished = bulk.outcome !== null;
    bulk.close();
    if (finished) onDone();
  };

  // The dialog stays mounted even when no row qualifies any more: a refetch
  // after the action moves the rows on, and the result view must survive it.
  return (
    <div className='sd-scope sd-font flex items-center gap-2'>
      {bulk.resendCount + bulk.rtoCount > 0 ? (
        <>
          <Button
            size='sm'
            variant='outline'
            disabled={bulk.resendCount === 0}
            onClick={() => bulk.open('resend')}
          >
            <RotateCw />
            {String(t('bulk_resend_button', { count: bulk.resendCount }))}
          </Button>
          <Button
            size='sm'
            variant='outline'
            disabled={bulk.rtoCount === 0}
            onClick={() => bulk.open('rto')}
          >
            <Undo2 />
            {String(t('bulk_request_rto_button', { count: bulk.rtoCount }))}
          </Button>
        </>
      ) : null}
      <BulkActionDialog
        action={bulk.action}
        plan={bulk.plan}
        outcome={bulk.outcome}
        failed={bulk.failed}
        note={bulk.note}
        onNoteChange={bulk.setNote}
        isSubmitting={bulk.isSubmitting}
        onSubmit={bulk.submit}
        onClose={close}
      />
    </div>
  );
}

export default BulkOrderActions;
